import { call, put, takeEvery } from 'redux-saga/effects';
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  logoutRequest,
  logoutSuccess,
  forgotPasswordRequest,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordFailure
} from '../actions/authActions';

import { authApi } from '../../api/authApi';
import { clearUser, setUser } from '../../../store/slices/userSlice';
import { toast } from 'react-toastify';

function* loginSaga(action) {
  try {
    yield put(loginRequest());
    const response = yield call(authApi.login, action.payload);
    
    // Normalize user data structure to handle both _id and id
    const normalizedUser = {
      ...response.user,
      _id: response.user._id || response.user.id,
      id: response.user.id || response.user._id
    };
    
    // Store tokens and normalized user in sessionStorage
    sessionStorage.setItem('authToken', response.accessToken || response.token);
    sessionStorage.setItem('refreshToken', response.refreshToken);
    sessionStorage.setItem('user', JSON.stringify(normalizedUser));
    
    yield put(loginSuccess(normalizedUser, response.accessToken || response.token));
    yield put(setUser(normalizedUser));
    toast.success('Login successful!', {
      toastId: "login-success"
    });
    
    // Navigation will be handled by LoginRoute component via React Router
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Login failed';
    yield put(loginFailure(errorMessage));
    toast.error(errorMessage, {
      toastId: "login-error"
    });
  }
}

function* logoutSaga() {
  try {
    yield put(logoutRequest());
    
    // Try to call logout API, but don't fail if it doesn't work
    try {
      yield call(authApi.logout);
    } catch (apiError) {
      // API logout failed, but continue with local cleanup
      console.warn('Logout API call failed:', apiError);
    }
    
    // Clear sessionStorage
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    
    // Clear Redux state
    yield put(logoutSuccess());
    yield put(clearUser());
    
    toast.success('Logged out successfully');
  } catch (error) {
    // Even if everything fails, clear local storage and state
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    yield put(logoutSuccess());
    yield put(clearUser());
  }
}

function* forgotPasswordSaga(action) {
  try {
    yield put(forgotPasswordRequest());
    const response = yield call(authApi.forgotPassword, action.payload.email);
    yield put(forgotPasswordSuccess(response.message));
    toast.success(response.message, {
      toastId: "forgot-password-success"
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to send password reset email';
    yield put(forgotPasswordFailure(errorMessage));
    toast.error(errorMessage, {
      toastId: "forgot-password-error"
    });
  }
}

function* resetPasswordSaga(action) {
  try {
    yield put(resetPasswordRequest());
    const response = yield call(authApi.resetPassword, action.payload);
    yield put(resetPasswordSuccess(response.message));
    toast.success(response.message, {
      toastId: "reset-password-success"
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password';
    yield put(resetPasswordFailure(errorMessage));
    toast.error(errorMessage, {
      toastId: "reset-password-error"
    });
  }
}

function* authSaga() {
  yield takeEvery('LOGIN_SAGA', loginSaga);
  yield takeEvery('LOGOUT_SAGA', logoutSaga);
  yield takeEvery('FORGOT_PASSWORD_SAGA', forgotPasswordSaga);
  yield takeEvery('RESET_PASSWORD_SAGA', resetPasswordSaga);
}

export default authSaga;
